import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { getOrderDisplayFromTo } from '@hero/core';
import { VehicleMainIcon, DateIcon, OrderRefIcon } from '@hero/web-icons';
import { VehicleName } from '../../Vehicle/VehicleName';
import { cn } from '@hero/web-lib';

export interface OrderInfoCardProps {
  /** The order to display information for */
  order: Order;
  /** Callback when card is clicked */
  onClick: () => void;
}

/**
 * OrderInfoCard - Bottom-center floating card showing order summary
 * Displays vehicle type, pickup date, and order reference
 * Clicking the card opens the full order details drawer
 */
export const OrderInfoCard: React.FC<OrderInfoCardProps> = ({ order, onClick }) => {
  const { t } = useTranslation('c-dates');

  const vehicleType = order.load.vehicles[0] || '';
  const orderRef = order.ref?.main || order.ref?.short || 'N/A';

  const translateDate = (key: string, fallback?: string): string => {
    return t(key, { defaultValue: fallback || key });
  };

  const pickupFromDate = order.dates.pickup;
  const pickupDateDisplay = getOrderDisplayFromTo({
    from: pickupFromDate.from,
    to: pickupFromDate.to,
    translate: translateDate,
  });

  return (
    <div
      onClick={onClick}
      className={cn(
        'absolute bottom-6 left-1/2 -translate-x-1/2 z-[100]',
        'bg-white/20 backdrop-blur-md rounded-xl shadow-lg hover:shadow-xl',
        'border border-gray-200/50',
        'px-5 py-3.5',
        'flex items-center gap-4',
        'cursor-pointer transition-all hover:scale-[1.02] hover:bg-white/30 hover:-translate-y-0.5',
        'w-auto max-w-[90vw]',
      )}>
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
          <VehicleMainIcon className="size-6 text-blue-600" />
        </div>
        <VehicleName
          vehicle={vehicleType}
          className="text-gray-900 text-base font-semibold whitespace-nowrap max-w-[150px] truncate"
        />
      </div>

      <div className="h-10 w-px bg-gray-200 flex-shrink-0" />

      <div className="flex items-center gap-2.5 flex-shrink-0">
        <DateIcon className="size-5 text-gray-600 flex-shrink-0" />
        <div className="flex flex-col gap-0.5 items-start">
          <span className="text-gray-900 text-sm font-semibold leading-tight whitespace-nowrap">
            {pickupDateDisplay.dateAutoOnly}
          </span>
          <span className="text-gray-600 text-xs font-medium leading-tight whitespace-nowrap">
            {pickupDateDisplay.timeOnly}
          </span>
        </div>
      </div>

      <div className="h-10 w-px bg-gray-200 flex-shrink-0" />

      <div className="flex items-center gap-2 flex-shrink-0">
        <OrderRefIcon className="size-5 text-gray-600 flex-shrink-0" />
        <span className="text-gray-900 text-base font-semibold whitespace-nowrap">{orderRef}</span>
      </div>
    </div>
  );
};
